import { useFormik } from "formik";
import * as Yup from "yup";
import AscButton from "../../AscButton/AscButton";
import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import AscModal from "../../AscModal/AscModal";
import { EyeIcon, FileIcon } from "../../AscIcons";
import styles from "./AscChallengeStudentList.module.scss";
import { AscFileViewModal, AscTextArea } from "../..";
import { useEffect, useState } from "react";
import {
  AscFile,
  getFileTypeFromExtension,
  normalizeFileSize,
} from "@ascendnow/shared";
import { formatErrorMessage } from "libs/components/src/lib";
import { useAscContext } from "libs/components/src/context";

interface AscChallengeCheckEssayModalProps {
  essayModal: string | boolean;
  setEssayModal: (value: string | boolean) => void;
  onClickApprove: (
    challengeSubmissionId: string,
    score: number,
    cb?: () => void
  ) => Promise<void>;
  selectedEssay: any;
  score: number;
}

export default function AscChallengeCheckEssayModal({
  essayModal,
  setEssayModal,
  onClickApprove,
  selectedEssay,
  score,
}: AscChallengeCheckEssayModalProps) {
  const [fileIdToShow, setFileIdToShow] = useState<string>();
  const { toast } = useAscContext();
  const formik = useFormik({
    initialValues: {
      score: score as number,
      response: selectedEssay?.submission?.body,
      files: [],
    },
    validationSchema: Yup.object({
      score: Yup.number().required("Please, give points to the essay"),
    }),
    onSubmit: (values) => {
      onClickApprove(essayModal as string, values.score, () =>
        setEssayModal(false)
      );
    },
  });

  useEffect(() => {
    if (selectedEssay) {
      formik.setFieldValue("response", selectedEssay?.submission?.body);
      formik.setFieldValue("files", selectedEssay?.files);
    }
    //eslint-disable-next-line
  }, [selectedEssay]);

  return (
    <AscModal
      visible={!!essayModal}
      setVisible={setEssayModal}
      modalClassName={styles.checkEssayModal}
      withBackground={false}
      header={<h5 className={styles.checkEssayTitle}>Check essay</h5>}
      onClose={() => setEssayModal(false)}
    >
      <form onSubmit={formik.handleSubmit} className={styles.checkEssay}>
        <div className={styles.checkEssayBody}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="response">
              Response
            </label>
            <AscTextArea
              id="response"
              disabled
              className={styles.inputField}
              placeholder="There must be the student's reponse"
              value={formik.values.response}
              onChange={formik.handleChange}
              rows={15}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="files">
              Files
            </label>
            {formik.values?.files?.length ? (
              <ul className={styles.filesList}>
                {formik.values?.files?.map((document: AscFile) => (
                  <li className={styles.fileItem} key={document?.id}>
                    <FileIcon className={styles.fileIcon} />
                    <div className={styles.fileTextContent}>
                      <div className={styles.fileName}>
                        {document?.fileName}
                      </div>
                      <div className={styles?.fileSize}>
                        {normalizeFileSize(document?.fileSize || 0)}
                      </div>
                      <span></span>
                    </div>
                    <AscButton
                      onClick={() => setFileIdToShow(document?.id)}
                      variant="white"
                      icon={<EyeIcon />}
                    />
                    <AscFileViewModal
                      visible={fileIdToShow === document?.id}
                      fileName={document?.fileName}
                      fileUrl={document?.location}
                      fileType={getFileTypeFromExtension(
                        document?.fileName?.split(".")[
                          document?.fileName?.split(".").length - 1
                        ]
                      )}
                      onClose={() => setFileIdToShow(undefined)}
                      onError={(e) =>
                        toast.error(
                          formatErrorMessage(e, "Error downloading file")
                        )
                      }
                      className={styles.essayFileView}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              "No files uploaded"
            )}
          </div>
          <div className={styles.fieldGroup} style={{ width: "50%" }}>
            <label className={styles.fieldLabel} htmlFor="score">
              Score
            </label>
            <AscTextInput
              type="number"
              id="score"
              className={styles.inputField}
              placeholder="Enter score"
              value={formik.values.score}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.score && formik.touched.score && (
              <div className={styles.fieldError}>{formik.errors.score}</div>
            )}
          </div>
        </div>
        <div className={styles.checkEssayFooter}>
          <AscButton
            type="button"
            variant="gray"
            onClick={() => setEssayModal(false)}
          >
            Cancel
          </AscButton>
          <AscButton type="submit" variant="primary">
            Approve
          </AscButton>
        </div>
      </form>
    </AscModal>
  );
}
